import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from "@angular/material/dialog";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { e164PhoneNumberRegex, normalizePhoneNumber } from "../../../core/validation/phone-validation";
import { DATE_ISO_FORMAT, DATE_SLASH_FORMAT } from "../../../core/formatting/date-formatting";
import { SnackbarService } from "../../../services/snackbar.service";
import { catchError, distinctUntilChanged, map, of, startWith, Subject, takeUntil } from "rxjs";
import { BlacklistService } from "../../../services/blacklist.service";
import * as moment from "moment";
import { normalizeEmail } from "../../../core/validation/email-validation";
import { normalizeLicenseId } from "../../../core/validation/license-validation";
import { addressCardValidator } from 'src/app/core/validation/address-card.validator';

@Component({
  selector: 'app-add-blacklist-item',
  templateUrl: './add-blacklist-item-dialog.component.html',
  styleUrls: ['./add-blacklist-item-dialog.component.scss']
})
export class AddBlacklistItemDialogComponent implements OnInit, OnDestroy {
  public submitAttemptedWithEmptyAddressCardNumber = false;
  public addressCardNumberIsEmpty = true;

  addBlacklistItemFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.email, Validators.required]),
    phoneNumber: new UntypedFormControl('', [Validators.pattern(e164PhoneNumberRegex), Validators.required]),
    licenseNumber: new UntypedFormControl('', [Validators.required]),
    birthDate: new UntypedFormControl('', [Validators.required]),
    reason: new UntypedFormControl('', [Validators.required]),
    addressCardNumber: new UntypedFormControl('', [addressCardValidator])
  });
  creationDate = moment();
  birthDateMax = moment();

  private onDestroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<AddBlacklistItemDialogComponent>,
    private snackbarService: SnackbarService,
    private blacklistService: BlacklistService
  ) { }

  ngOnInit(): void {
    this.setAddressCardNumberIsEmpty();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onSubmit() {
    if (this.addBlacklistItemFormGroup.valid) {
      let addressCardNumber = this.addBlacklistItemFormGroup.get('addressCardNumber')?.value?.trim?.() || undefined;

      if (!addressCardNumber && !this.submitAttemptedWithEmptyAddressCardNumber) {
        this.submitAttemptedWithEmptyAddressCardNumber = true;
        return;
      }

      let email = normalizeEmail(this.addBlacklistItemFormGroup.get('email')?.value);
      let phoneNumber = normalizePhoneNumber(this.addBlacklistItemFormGroup.get('phoneNumber')?.value);
      let licenseNumber = normalizeLicenseId(this.addBlacklistItemFormGroup.get('licenseNumber')?.value);
      let birthDate = this.addBlacklistItemFormGroup.get('birthDate')?.value.format(DATE_ISO_FORMAT);
      let reason = this.addBlacklistItemFormGroup.get('reason')?.value;

      this.blacklistService.createBlacklistItem$({
        email: email,
        phoneNumber: phoneNumber,
        addressCardNumber,
        license: licenseNumber,
        dateOfBirth: birthDate,
        reason: reason
      })
        .pipe(
          catchError(() => {
            this.snackbarService.error(`”${reason} - ${this.formattedCreationDate()}” feketelistához adása során hiba lépett fel!`);
            this.dialogRef.close({ success: false });
            return of();
          })
        )
        .subscribe(() => {
          this.snackbarService.success(`”${reason} - ${this.formattedCreationDate()}” sikeresen hozzáadva a feketelistához!`)
          this.dialogRef.close({ success: true });
        })
    }
  }

  formattedCreationDate() {
    return this.creationDate.format(DATE_SLASH_FORMAT)
  }

  private setAddressCardNumberIsEmpty(): void {
    this.addBlacklistItemFormGroup.controls['addressCardNumber'].valueChanges
    .pipe(
      startWith(this.addBlacklistItemFormGroup.controls['addressCardNumber'].value),
      map((value) => !value?.trim?.()?.length),
      distinctUntilChanged(),
      takeUntil(this.onDestroy$)
    ).subscribe((empty) => {
      this.addressCardNumberIsEmpty = empty;
    });
  }
}
